import * as contentful from 'contentful';
import type { ContentfulGraphQLData, ContentfulGraphQLError } from '@/types/contentful';

type ContentfulSys = {
  id: string;
};

// This is not actually the page type, but the type of the content field in the page
export type ContentPage = {
  sys: ContentfulSys;
};

export type ContentListingPage = {
  fields: {
    content?: ContentPage[];
    slug: string;
  };
};

type ContentfulArguments = {
  [key: string]: string | boolean | number | undefined | null;
};

type Contentfuloptions = {
  reportErrors?: boolean;
};

const handleErrors = (errors: ContentfulGraphQLError[]) => {
  // Filter out error, that we know not to be a problem like draft/deleted content
  const rv = [];

  for (const error of errors) {
    if (error?.extensions?.contentful && error?.extensions.contentful.code !== 'UNRESOLVABLE_LINK') {
      rv.push(error);
    }
  }

  return rv;
};

export const useContentful = () => {
  // https://github.com/contentful/contentful.js/issues/422
  if (!process.server) {
    window.process = { browser: true };
  }
  const { $sentry, $httpsProxyAgent, $httpsProxyAgentFetch } = useNuxtApp();
  const config = useRuntimeConfig();
  const isDev = config.env === 'development';
  const showPreview = config.public.contentful.environment === 'stage';
  const contentfulConfig = {
    ...config.public.contentful,
    httpsAgent: $httpsProxyAgent,
  };
  const client = contentful.createClient(contentfulConfig);

  const graphQLEndpoint = computed(
    () =>
      `https://graphql.contentful.com/content/v1/spaces/${contentfulConfig.space}/environments/${contentfulConfig.environment}`,
  );

  const fetchTags = async (options = { reportErrors: true }) => {
    try {
      const response = await client.getTags();
      return { data: response };
    } catch (error) {
      if (options.reportErrors && !isDev) $sentry.captureException(error);
      return { error: { statusCode: 404, message: error } };
    }
  };

  const fetchEntry = async (args: ContentfulArguments, options?: Contentfuloptions) => {
    const res = await fetchEntries(args, options);

    return { data: { ...res.data?.[0] }, error: res.error };
  };

  const fetchEntryWithGraphQL = async <T>(query: string, variables = {}) => {
    try {
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${contentfulConfig.accessToken}`,
        },
        body: JSON.stringify({
          query,
          variables: { ...variables, preview: showPreview },
        }),
      };
      if (process.server && $httpsProxyAgentFetch) fetchOptions.dispatcher = $httpsProxyAgentFetch;
      const data = await $fetch<ContentfulGraphQLData<T>>(graphQLEndpoint.value, fetchOptions);

      const errors = (data?.errors && handleErrors(data?.errors)) || null;

      return { data: data?.data, error: errors };
    } catch (error) {
      return { data: null, error };
    }
  };

  const fetchEntries = async (
    { contentType, include, select = null, ...args }: ContentfulArguments,
    options: Contentfuloptions = { reportErrors: true },
  ) => {
    try {
      const contentTypeArg = contentType ? { content_type: contentType } : {};
      if (!process.server) process.browser = true;
      const response = await client.getEntries({ ...contentTypeArg, include, select, ...args });

      if (!response.items.length || (response.items.length === 1 && !response.items[0].fields)) {
        throw new Error('Content not found');
      }

      if (response.errors) {
        const failedIds: string[] = [];

        response.errors.forEach((error) => {
          if (error.sys.id !== 'notResolvable') {
            /* Do not report errors that indicate draft/deleted content
              The unresolved entries are excluded from the resulting items-array due to "removeUnresolved"-flag in the ctf client config */
            failedIds.push(error.details?.id.toString());
          }
        });
        if (!failedIds.length) {
          return { data: JSON.parse(JSON.stringify(response.items)) };
        }
        const errorMessage = `Unresolvable CMS content - ids: ${failedIds}`;

        if (options.reportErrors && !isDev && failedIds.length)
          $sentry.captureException(errorMessage, {
            tags: {
              service: 'cms',
            },
          });
        return {
          data: JSON.parse(JSON.stringify(response.items)),
          error: { statusCode: undefined, message: errorMessage },
        };
      }

      // Deep copy items to not keep reference to original object
      return {
        data: JSON.parse(JSON.stringify(response.items)),
      };
    } catch (error) {
      // if (options.reportErrors && !isDev) $sentry.captureException(error);
      return { error: { statusCode: 404, message: error } };
    }
  };

  const fullTextPageSearch = async (contentListingPages: ContentListingPage[], query: string) => {
    const availableContentPages = contentListingPages
      .flatMap((lPage: ContentListingPage) => lPage.fields.content?.map((cPage: ContentPage) => cPage.sys.id))
      .filter((id) => id)
      .join(',');

    const contentPages = await fetchEntries(
      {
        content_type: 'contentPage',
        'fields.excludeFromSearch': false,
        'sys.id[in]': availableContentPages,
        include: 2,
        query,
        limit: 5,
      },
      { reportErrors: false },
    );

    if (contentPages.error) return { error: true };
    else {
      const contentPageData = contentPages.data || [];
      const pages = [...contentPageData];

      const data = pages.map((cPage) => {
        const parentListingPage: ContentListingPage | undefined = contentListingPages.find(
          (lPage: ContentListingPage) => lPage.fields.content?.find((c: ContentPage) => c.sys.id === cPage.sys.id),
        );
        return {
          title: cPage.fields.title,
          preTitle: cPage.fields.preTitle,
          slug: `/${(parentListingPage as ContentListingPage).fields.slug}/${cPage.fields.slug}`,
        };
      });
      return { data };
    }
  };

  return {
    fetchTags,
    fetchEntry,
    fetchEntryWithGraphQL,
    fetchEntries,
    fullTextPageSearch,
  };
};
